import { Panel } from "../../../../components/panels/Panel";
import { Bar } from "react-chartjs-2";
import { DateRangePicker } from "@heroui/date-picker";
import { useEffect, useState } from "react";
import { calendarDateTimetoDate, formatDateTimeFromTimestamp } from "../../../../helpers/date/date-helper";
import {
  createDateRange,
} from "../../../../services/ergonomics/ergonomics-service";
import { InputSelect } from "../../../../components/forms/inputs/InputSelect";
import { getInfluxdbData } from "../../../../services/api/influxdb-tools";
import { formatSeriesByShift } from "../../../../services/ergonomics/ergonomics-requests";
import { PlayIcon } from "@heroicons/react/20/solid";
import { useOrgData } from "../../../../contexts/OrgDataContext";

export const DailyDoubleBarChart = ({ startDate, endDate, seriesA, seriesB, labels, metrics }) => {
  const days = [];
  for (let d = new Date(startDate); d <= new Date(endDate); d.setDate(d.getDate() + 1)) {
    days.push(new Date(d.getFullYear(), d.getMonth(), d.getDate())); // minuit local
  }

  const findValueForDay = (series, day) => {
    const dayStart = new Date(day.getFullYear(), day.getMonth(), day.getDate()).getTime();
    const dayEnd = dayStart + 24 * 60 * 60 * 1000;

    for (const p of series) {
      if (!p?.time) continue;
      const t = new Date(p.time).getTime();
      if (t >= dayStart && t < dayEnd) return p.value;
    }

    return null;
  };

  const valuesA = days.map((d) => findValueForDay(seriesA, d));
  const valuesB = days.map((d) => findValueForDay(seriesB, d));
  const labelsDays = days.map((d) =>
    d.toLocaleDateString("fr-FR", { day: "2-digit", month: "2-digit" })
  );

  const data = {
    labels: labelsDays,
    datasets: [
      {
        label: labels[0],
        data: valuesA,
        borderRadius: 6,
        borderColor: (context) => getBarColor(context),
        backgroundColor: (context) => getBarColor(context),
        categoryPercentage: 0.6,
        barPercentage: 0.7,
      },
      {
        label: labels[1],
        data: valuesB,
        borderRadius: 6,
        borderColor: (context) => getBarColor(context),
        backgroundColor: (context) => getBarColor(context),
        categoryPercentage: 0.6,
        barPercentage: 0.7,
      },
    ],
  };

  const options = {
    maintainAspectRatio: false,
    responsive: true,
    scales: {
      x: {
        stacked: false,
        grid: { display: false },
        ticks: { color: "#444" },
      },
      y: {
        beginAtZero: true,
        grid: { color: "rgba(200,200,200,0.2)" },
      },
    },
    plugins: {
      legend: { display: false },
    },
  };

  const getBarColor = (context) => {
    const value = context.raw ?? 0;

    if (metrics.value === 'reba_shift_mean') {
      if (value >= 8) return 'rgba(255, 99, 132, 0.8)'; // Red
      else if (value >= 3) return 'rgba(255, 206, 86, 0.8)'; // Yellow
      else return 'rgba(75, 255, 192, 0.8)'; // Green
    }
    else {
      if (value >= 80) return "rgba(75, 255, 192, 0.8)";   // Green
      else if (value >= 60) return "rgba(255, 206, 86, 0.8)"; // Yellow
      else if (value >= 40) return "rgba(255, 150, 86, 0.8)"; // Orange
      else if (value >= 20) return "rgba(255, 99, 132, 0.8)"; // Red
      else return "rgba(255, 120, 150, 0.9)";                // Bright red
    }
  };

  return (
    <div className='h-full w-full'>
      <Bar data={data} options={options}/>
    </div>
  );
};


export const ErgonomicsCardDataGraph = () => {

  const METRICS = [
    {id: 1, label: 'LA MOYENNE REBA', value: 'reba_shift_mean'},
    {id: 2, label: 'L\'INDICE DE FATIGUE', value: 'reba_fatigue_index_shift'},
  ];
  const [usedMetrics, setUsedMetrics] = useState(METRICS[0]);
  const [displayedMetrics, setDisplayedMetrics] = useState(METRICS[0]);

  const { workstations } = useOrgData();
  const [workstationSelected, setWorkstationSelected] = useState(workstations?.[0]);

  const [morningSeries, setMorningSeries] = useState([]);
  const [afternoonSeries, setAfternoonSeries] = useState([]);

  const now = new Date();
  const nextHour = new Date(now);
  nextHour.setMinutes(0);
  nextHour.setSeconds(0);
  nextHour.setMilliseconds(0);
  nextHour.setHours(0);
  const yesterday = new Date(nextHour);
  const prevWeekDay = new Date(nextHour);
  yesterday.setDate(prevWeekDay.getDate() - 1);
  yesterday.setHours(23);
  yesterday.setMinutes(59);
  prevWeekDay.setDate(prevWeekDay.getDate() - 8);

  const [dateRange, setDateRange] = useState({
    start: prevWeekDay.getTime(),
    stop: yesterday.getTime(),
  });

  const [range, setRange] = useState({
    start: formatDateTimeFromTimestamp(dateRange.start),
    end: formatDateTimeFromTimestamp(dateRange.stop),
  });

  const loadData = () => {
    const startTimestamp = calendarDateTimetoDate(range.start).getTime();
    const stopTimestamp = calendarDateTimetoDate(range.end).getTime();
    setDateRange(createDateRange(startTimestamp, stopTimestamp));
    getData(startTimestamp, stopTimestamp);
  };

  const getData = async (start = null, stop = null) => {
    const reqStart = start !== null ? start : dateRange.start;
    const reqStop = stop !== null ? stop : dateRange.stop;
    let newData = await getInfluxdbData(
      'engine',
      'results',
      [['client_id', '==', workstationSelected?.device?.stream_id]],
      [usedMetrics.value],
      Math.floor(reqStart / 1000),
      Math.floor(reqStop / 1000),
      null,
      null,
      null,
      [],
      null,
      null,
      false,
      null,
    );

    const { morning, afternoon } = formatSeriesByShift(newData, usedMetrics.value);
    setMorningSeries(morning);
    setAfternoonSeries(afternoon);
    setDisplayedMetrics(usedMetrics);
  };

  useEffect(() => {
    getData();
  }, []);

  return (
    <Panel title="HISTORIQUE DES INDICATEURS">
      <div className="flex justify-between mb-4">
        <div className="flex justify-start items-baseline gap-4">
          <div className="flex gap-4">
            <div className="w-64">
              <InputSelect
                items={METRICS}
                onChange={setUsedMetrics}
                itemToString={(item) => item?.label}
                defaultValue={usedMetrics}
              />
            </div>
            <div className="w-64">
              <InputSelect
                items={workstations}
                onChange={setWorkstationSelected}
                itemToString={(item) => item?.name}
                defaultValue={workstationSelected}
              />
            </div>
          </div>

          <DateRangePicker
            classNames={{
              base: 'p-0 gap-0',
              inputWrapper: 'w-full bg-transparent hover:bg-transparent focus-within:hover:bg-transparent text-white cursor-default p-0 min-h-0 h-auto',
              innerWrapper: 'flex flex-wrap',
              label: 'text-white text-sm w-auto',
              input: 'bg-perception-gray-700 rounded-md px-2 w-auto'
            }}
            value={{
              start: range.start,
              end: range.end,
            }}
            className="w-full max-w-full" radius="none" granularity="day" label={null}
            onChange={({ start, end }) => setRange({ start, end })}
          />
        </div>
        <button className="text-xs text-white bg-perception-blue py-1 px-2 flex items-center rounded-md" onClick={loadData}>
          <PlayIcon aria-hidden="true" className="size-4 mr-2" /> Charger
        </button>
      </div>
      <DailyDoubleBarChart
        startDate={dateRange.start}
        endDate={dateRange.stop}
        seriesA={morningSeries}
        seriesB={afternoonSeries}
        labels={['Equipe matin', 'Equipe après-midi']}
        metrics={displayedMetrics}
      />
    </Panel>
  );
};